import { CircleCheckIcon, LockIcon, MailIcon, Trash2Icon } from 'lucide-react'
import type { ComponentProps, ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { UserStatus } from '@/api/users'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type BadgeVariant = ComponentProps<typeof Badge>['variant']

/**
 * `ACTIVE` is deliberately the quietest of the four. It is the state most rows are in, and a table
 * where every row shouts tells you nothing about the one row that should.
 */
const APPEARANCE: Record<
  UserStatus,
  { variant: BadgeVariant; icon: ComponentType<{ className?: string }> }
> = {
  [UserStatus.ACTIVE]: { variant: 'secondary', icon: CircleCheckIcon },
  [UserStatus.INVITED]: { variant: 'outline', icon: MailIcon },
  [UserStatus.LOCKED]: { variant: 'destructive', icon: LockIcon },
  [UserStatus.DELETED]: { variant: 'ghost', icon: Trash2Icon },
}

const LABELS: Record<UserStatus, 'users.status.ACTIVE' | 'users.status.INVITED' | 'users.status.LOCKED' | 'users.status.DELETED'> = {
  [UserStatus.ACTIVE]: 'users.status.ACTIVE',
  [UserStatus.INVITED]: 'users.status.INVITED',
  [UserStatus.LOCKED]: 'users.status.LOCKED',
  [UserStatus.DELETED]: 'users.status.DELETED',
}

const HINTS: Record<
  UserStatus,
  'users.statusHint.ACTIVE' | 'users.statusHint.INVITED' | 'users.statusHint.LOCKED' | 'users.statusHint.DELETED'
> = {
  [UserStatus.ACTIVE]: 'users.statusHint.ACTIVE',
  [UserStatus.INVITED]: 'users.statusHint.INVITED',
  [UserStatus.LOCKED]: 'users.statusHint.LOCKED',
  [UserStatus.DELETED]: 'users.statusHint.DELETED',
}

/**
 * The status is derived by the backend from Keycloak, and the four names on their own do not say
 * much — "invited" in particular reads as "done" when it means "cannot log in yet". Hence the
 * tooltip.
 */
export function UserStatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation()
  const { variant, icon: Icon } = APPEARANCE[status]

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={variant} className="gap-1">
          <Icon aria-hidden />
          {t(LABELS[status])}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{t(HINTS[status])}</TooltipContent>
    </Tooltip>
  )
}
